// Two-needle gauge (M03, docs/maturity/03 "Dashboard"): neutral track, fixed colours with a legend, both values
// always printed as text, no needle at all for a missing scenario (never a fake zero).
import { GAUGE_COLORS, GAUGE_TRACK, gaugePoint } from "../../../shared/maturity/gauge.ts";
import { formatPercent, type ScoreSummary } from "../../../shared/maturity/scoring.ts";
import { cx } from "../ui.tsx";

type S = Pick<ScoreSummary, "percent" | "fraction"> | null | undefined;

export function DualGauge({ diagnosis, prognosis, label, size = 180, showPrognosis = true, showDiagnosis = true }: {
  diagnosis: S; prognosis: S; label: string; size?: number; showPrognosis?: boolean; showDiagnosis?: boolean;
}) {
  const w = 160;
  const h = 92;
  const cx0 = w / 2;
  const cy = h - 6;
  const r = w / 2 - 10;
  const a = gaugePoint(0, cx0, cy, r);
  const b = gaugePoint(100, cx0, cy, r);
  const needle = (s: S, color: string, dashed: boolean, name: string) => {
    if (!s || s.percent === null) return null;
    const p = gaugePoint(s.percent, cx0, cy, r - 6);
    return <line data-needle={name} x1={cx0} y1={cy} x2={p.x} y2={p.y} stroke={color} strokeWidth={3.5} strokeLinecap="round" strokeDasharray={dashed ? "5 3" : undefined}
      className="transition-[x2,y2] duration-[var(--duration-slow)] motion-reduce:transition-none" />;
  };
  const text = [
    showDiagnosis ? `diagnóstico ${diagnosis?.fraction ? formatPercent(diagnosis) : "pendente"}` : null,
    showPrognosis ? `prognóstico ${prognosis?.fraction ? formatPercent(prognosis) : "sem registro"}` : null,
  ].filter(Boolean).join(", ");
  return (
    <svg viewBox={`0 0 ${w} ${h}`} width={size} height={(size * h) / w} role="img" aria-label={`${label}: ${text}`} className="max-w-full">
      <path d={`M ${a.x} ${a.y} A ${r} ${r} 0 0 1 ${b.x} ${b.y}`} fill="none" stroke={GAUGE_TRACK} strokeWidth={10} strokeLinecap="round" />
      {[0, 50, 100].map((t) => {
        const o = gaugePoint(t, cx0, cy, r + 7);
        const i = gaugePoint(t, cx0, cy, r - 7);
        return <line key={t} x1={i.x} y1={i.y} x2={o.x} y2={o.y} stroke="#9AAABD" strokeWidth={1} />;
      })}
      {showDiagnosis && needle(diagnosis, GAUGE_COLORS.diagnosis, false, "diagnosis")}
      {showPrognosis && needle(prognosis, GAUGE_COLORS.prognosis, true, "prognosis")}
      <circle cx={cx0} cy={cy} r={4.5} fill="#13253D" />
    </svg>
  );
}

export function GaugeLegend({ className, showPrognosis = true, showDiagnosis = true }: { className?: string; showPrognosis?: boolean; showDiagnosis?: boolean }) {
  return (
    <div className={cx("flex flex-wrap items-center gap-x-5 gap-y-1 text-xs text-muted", className)}>
      {showDiagnosis && <span className="inline-flex items-center gap-1.5"><span className="inline-block w-6 border-t-[3px]" style={{ borderColor: GAUGE_COLORS.diagnosis }} aria-hidden />Diagnóstico (como estamos)</span>}
      {showPrognosis && <span className="inline-flex items-center gap-1.5"><span className="inline-block w-6 border-t-[3px] border-dashed" style={{ borderColor: GAUGE_COLORS.prognosis }} aria-hidden />Prognóstico (como poderemos ficar)</span>}
    </div>
  );
}
